import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Filter } from 'src/app/model/search-filters/filter';

/**
 * Lets the user attach many {@link Filter}s to a search-filter item.
 *
 * Uses an Angular Material multiple `mat-select` so the dropdown stays open
 * while several options are toggled with checkboxes. A search box and
 * "select/deselect all matching" actions are projected into the panel to make
 * bulk selection of related filters (e.g. everything containing `activity`)
 * quick. The public `filters` in/out API stays a plain `Filter[]`, so existing
 * form persistence and submission remain unchanged.
 */
@Component({
  selector: 'gymfinder-filter-select',
  templateUrl: './filter-select.component.html',
  styleUrls: ['./filter-select.component.scss'],
  standalone: false,
})
export class FilterSelectComponent implements OnInit, OnChanges, AfterViewInit {
  @Input()
  filters: Filter[] = [];

  @Output()
  filtersChange = new EventEmitter<Filter[]>();

  @Input()
  availableFilters: Filter[] = [];

  /** All selectable options (available ∪ already-selected), deduped, sorted. */
  optionFilters: Filter[] = [];

  /** Ids of the currently selected filters — the value bound to the select. */
  selectedIds: string[] = [];

  /** Free-text term used to narrow the list of options. */
  searchTerm = '';

  /** Options rendered for the current term (always includes selected ones). */
  visibleFilters: Filter[] = [];

  /** Options matching the current term, regardless of selection (bulk target). */
  matchingFilters: Filter[] = [];

  allMatchingSelected = false;

  someMatchingSelected = false;

  /** Selected filters, in option order, for the trigger summary. */
  selectedFilters: Filter[] = [];

  @ViewChild('searchInput')
  searchInput?: ElementRef<HTMLInputElement>;

  @ViewChild('select')
  select?: MatSelect;

  private selectedIdSet = new Set<string>();

  ngOnInit(): void {
    this.rebuildOptions();
  }

  ngAfterViewInit(): void {
    this.scrollOptionsWithinList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableFilters'] || changes['filters']) {
      this.rebuildOptions();
    }
  }

  onSelectionChange(ids: string[]): void {
    this.setSelectedIds(ids);
    this.emitSelection();
  }

  removeFilter(filter: Filter): void {
    if (!filter.id) {
      return;
    }

    this.setSelectedIds(this.selectedIds.filter(id => id !== filter.id));
    this.emitSelection();
  }

  selectAllMatching(): void {
    const matchingIds = this.matchingFilters
      .map(f => f.id)
      .filter((id): id is string => !!id);

    this.setSelectedIds([...this.selectedIds, ...matchingIds]);
    this.emitSelection();
  }

  deselectAllMatching(): void {
    const matchingIds = new Set(this.matchingFilters.map(f => f.id));

    this.setSelectedIds(this.selectedIds.filter(id => !matchingIds.has(id)));
    this.emitSelection();
  }

  onSearchTermChange(term: string | null): void {
    this.searchTerm = term ?? '';
    this.recomputeDerivedState();
  }

  clearSearch(): void {
    this.onSearchTermChange('');
    this.searchInput?.nativeElement.focus();
  }

  onPanelToggled(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.searchInput?.nativeElement.focus());
    } else {
      this.onSearchTermChange('');
    }
  }

  /**
   * Keep search typing inside the input, but let navigation keys reach the
   * select so keyboard users can still move to and toggle options.
   */
  onSearchKeydown(event: KeyboardEvent): void {
    const passThrough = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];
    if (!passThrough.includes(event.key)) {
      event.stopPropagation();
    }
  }

  trackByFilter = (_: number, filter: Filter): string =>
    filter.id ?? filter.slug ?? '';

  /**
   * The options scroll in their own container rather than in the panel, so the
   * header can never be scrolled over (see `.filter-select-panel` in
   * `styles.scss`). `MatSelect` still drives `panel.scrollTop` — now inert — to
   * reveal the first selected option on open and the active option during
   * keyboard navigation, so point that at the list instead.
   */
  private scrollOptionsWithinList(): void {
    const select = this.select;
    if (!select) {
      return;
    }

    select._scrollOptionIntoView = (index: number) => {
      const panel: HTMLElement | undefined = select.panel?.nativeElement;
      const list = panel?.querySelector<HTMLElement>(
        '.filter-select-panel__options'
      );
      const option = select.options.toArray()[index]?._getHostElement();
      if (!list || !option) {
        return;
      }

      const top = option.offsetTop;
      const bottom = top + option.offsetHeight;

      if (top < list.scrollTop) {
        list.scrollTop = top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    };
  }

  private emitSelection(): void {
    const byId = new Map(this.optionFilters.map(f => [f.id, f]));
    this.filters = this.selectedIds
      .map(id => byId.get(id))
      .filter((f): f is Filter => !!f);

    this.filtersChange.emit(this.filters);
  }

  private rebuildOptions(): void {
    const byId = new Map<string, Filter>();
    for (const filter of this.availableFilters ?? []) {
      if (filter.id) {
        byId.set(filter.id, filter);
      }
    }
    for (const filter of this.filters ?? []) {
      if (filter.id && !byId.has(filter.id)) {
        byId.set(filter.id, filter);
      }
    }

    this.optionFilters = Array.from(byId.values()).sort((a, b) =>
      (a.slug ?? '').localeCompare(b.slug ?? '')
    );

    this.setSelectedIds(
      (this.filters ?? []).map(f => f.id).filter((id): id is string => !!id)
    );
  }

  private setSelectedIds(ids: string[]): void {
    this.selectedIds = Array.from(new Set(ids));
    this.selectedIdSet = new Set(this.selectedIds);
    this.recomputeDerivedState();
  }

  private recomputeDerivedState(): void {
    const term = this.normalize(this.searchTerm);

    this.matchingFilters = term
      ? this.optionFilters.filter(f => this.optionMatches(f, term))
      : this.optionFilters.slice();

    this.visibleFilters = term
      ? this.optionFilters.filter(
          f => this.isSelected(f) || this.optionMatches(f, term)
        )
      : this.optionFilters.slice();

    this.allMatchingSelected =
      this.matchingFilters.length > 0 &&
      this.matchingFilters.every(f => this.isSelected(f));
    this.someMatchingSelected = this.matchingFilters.some(f =>
      this.isSelected(f)
    );

    this.selectedFilters = this.optionFilters.filter(f => this.isSelected(f));
  }

  private isSelected(filter: Filter): boolean {
    return !!filter.id && this.selectedIdSet.has(filter.id);
  }

  private optionMatches(filter: Filter, normalizedTerm: string): boolean {
    return this.normalize(filter.slug ?? '').includes(normalizedTerm);
  }

  private normalize(value: string): string {
    return (value ?? '').trim().toLowerCase();
  }
}
